import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Radio, RadioGroup, FormControlLabel, FormControl,
    Autocomplete, CircularProgress, Snackbar, Alert, Checkbox,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ClearIcon from "@mui/icons-material/Clear";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import AccGroupSelect from "../components/AccGroupSelect";
import {
    FB, FB_LIGHT, FB_MID, BORDER, T1, T2,
    G200, G300, G500, G700, G900, WHITE,
    btnGhostSx, btnPrimarySx, btnOutlineSx, inputSx,
} from "./ReportDetailsConstants";

const getTodayDate = () => new Date().toISOString().split("T")[0];

// Indian FY runs April 1 -> March 31 — same default range convention used by
// Depreciation Report / General Ledger's default filters.
const getFYDates = () => {
    const today = new Date();
    const month = today.getMonth(); // 0 = Jan … 3 = Apr
    const year = today.getFullYear();
    const fyStartYear = month >= 3 ? year : year - 1;
    return {
        fromDate: `${fyStartYear}-04-01`,
        toDate: `${fyStartYear + 1}-03-31`,
    };
};

// ── InterestCalculationFilterDialog — same visual design as
// DepreciationReportFilterDialog (General Ledger's SmartFilterDialog family):
// left-accent header + title/subtitle + close icon, uppercase field labels,
// Preview chips, Cancel/Get Data footer actions. Mirrors the VB.NET screen's
// fields exactly: Groupwise/Accountwise radio, From/To Date, Account Group
// (Groupwise only), Acc. Name (Accountwise only), Credit/Debit Interest %,
// Divide Factor, Due Date wise checkbox.
function InterestCalculationFilterDialog({ open, onClose, connectionString, companyId, onOk }) {
    const fyDates = getFYDates();
    const [mode, setMode] = useState("accountwise"); // "groupwise" | "accountwise"
    const [fromDate, setFromDate] = useState(fyDates.fromDate);
    const [toDate, setToDate] = useState(fyDates.toDate);

    // Account Group (Groupwise mode) — same AccGroupSelect component + same
    // GET /api/client-get-data/api/AccUnderGroup endpoint ClientMaster.jsx uses.
    const [accGroups, setAccGroups] = useState([]);
    const [accGroupsLoading, setAccGroupsLoading] = useState(false);
    const [selectedAccGrpId, setSelectedAccGrpId] = useState("");
    const [selectedAccGrpName, setSelectedAccGrpName] = useState("");

    // Acc. Name (Accountwise mode) — same /api/company-search pattern used by
    // DepreciationReport/BankCashPosting/DepreciationMaster.
    const [accNameValue, setAccNameValue] = useState(null);
    const [accNameInput, setAccNameInput] = useState("");
    const [accNameOptions, setAccNameOptions] = useState([]);
    const [accNameLoading, setAccNameLoading] = useState(false);
    const [accNameDropdownOpen, setAccNameDropdownOpen] = useState(false);

    const [creditInterestPct, setCreditInterestPct] = useState("18");
    const [debitInterestPct, setDebitInterestPct] = useState("18");
    const [divideFactor, setDivideFactor] = useState("365");
    const [dueDateWise, setDueDateWise] = useState(false);

    // Reset to defaults every time the dialog is opened
    useEffect(() => {
        if (open) {
            const d = getFYDates();
            setMode("accountwise");
            setFromDate(d.fromDate);
            setToDate(d.toDate);
            setSelectedAccGrpId("");
            setSelectedAccGrpName("");
            setAccNameValue(null);
            setAccNameInput("");
            setCreditInterestPct("18");
            setDebitInterestPct("18");
            setDivideFactor("365");
            setDueDateWise(false);
        }
    }, [open]);

    // Load all account groups for this company — same
    // GET /api/client-get-data/api/AccUnderGroup endpoint + fetch pattern as
    // ClientMaster.jsx's Hlp_Fn_AccUnderGroup, feeding the same AccGroupSelect
    // component used there.
    useEffect(() => {
        if (!open || !companyId) return;
        let active = true;
        (async () => {
            setAccGroupsLoading(true);
            try {
                const { data } = await axios.get(`${connectionString}/api/client-get-data/api/AccUnderGroup`, {
                    params: { companyId },
                    headers: { "Cache-Control": "no-cache" },
                });
                if (active) setAccGroups(data || []);
            } catch (err) {
                if (active) setAccGroups([]);
            } finally {
                if (active) setAccGroupsLoading(false);
            }
        })();
        return () => { active = false; };
    }, [open, companyId, connectionString]);

    // Search "Acc. Name" as user types
    useEffect(() => {
        if (mode !== "accountwise") return;
        if (!accNameDropdownOpen && (!accNameInput || accNameInput.trim() === "")) {
            setAccNameOptions([]);
            return;
        }
        let active = true;
        const timer = setTimeout(async () => {
            setAccNameLoading(true);
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: "FirmName", search: accNameInput, companyId },
                });
                if (active) {
                    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                    setAccNameOptions(rows);
                }
            } catch (err) {
                if (active) setAccNameOptions([]);
            } finally {
                if (active) setAccNameLoading(false);
            }
        }, 300);
        return () => { active = false; clearTimeout(timer); };
    }, [mode, accNameInput, accNameDropdownOpen, companyId, connectionString]);

    const canSubmit = mode === "groupwise" ? !!selectedAccGrpId : !!accNameValue;

    const handleOk = () => {
        if (!canSubmit) return;
        onOk({
            mode,
            fromDate,
            toDate,
            accGrpId: selectedAccGrpId || null,
            accGrpLabel: selectedAccGrpName || "",
            partyId: accNameValue?.id || null,
            partyLabel: accNameValue?.FirmName || "",
            creditInterestPct: Number(creditInterestPct) || 0,
            debitInterestPct: Number(debitInterestPct) || 0,
            divideFactor: Number(divideFactor) || 365,
            dueDateWise,
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: "10px", border: `1px solid ${G300}`, boxShadow: "0 8px 40px rgba(0,0,0,0.12)" } }}
        >
            {/* ── Dialog Title ── */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderBottom: `1px solid ${G300}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 3, height: 22, background: FB, borderRadius: 2 }} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: G900, lineHeight: 1 }}>Interest Calculation</Typography>
                            <Typography sx={{ fontSize: 11, color: G500, mt: "2px" }}>Configure report parameters</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: G500, "&:hover": { background: G200 } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* ── Dialog Content ── */}
            <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* ── Groupwise / Accountwise ── */}
                    <FormControl>
                        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                            <FormControlLabel
                                value="groupwise"
                                control={<Radio size="small" sx={{ color: FB_MID, "&.Mui-checked": { color: FB } }} />}
                                label={<Typography sx={{ fontSize: 14, fontWeight: 600, color: T1 }}>Groupwise</Typography>}
                            />
                            <FormControlLabel
                                value="accountwise"
                                control={<Radio size="small" sx={{ color: FB_MID, "&.Mui-checked": { color: FB } }} />}
                                label={<Typography sx={{ fontSize: 14, fontWeight: 600, color: T1 }}>Accountwise</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* ── From Date / To Date ── */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                From Date
                            </Typography>
                            <TextField type="date" fullWidth size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={inputSx} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                To Date
                            </Typography>
                            <TextField type="date" fullWidth size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} sx={inputSx} />
                        </Box>
                    </Box>

                    {/* ── Divider ── */}
                    <Box sx={{ height: "1px", background: BORDER, mx: -0.5 }} />

                    {/* ── Account Group (Groupwise) ── */}
                    {mode === "groupwise" && (
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                Account Group
                            </Typography>
                            <AccGroupSelect
                                groups={accGroups}
                                value={selectedAccGrpId}
                                loading={accGroupsLoading}
                                onChange={(id, name) => {
                                    setSelectedAccGrpId(String(id ?? ""));
                                    setSelectedAccGrpName(String(name ?? ""));
                                }}
                            />
                        </Box>
                    )}

                    {/* ── Acc. Name (Accountwise) ── */}
                    {mode === "accountwise" && (
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                Acc. Name
                            </Typography>
                            <Autocomplete
                                fullWidth
                                options={accNameOptions}
                                loading={accNameLoading}
                                open={accNameDropdownOpen}
                                onOpen={() => setAccNameDropdownOpen(true)}
                                onClose={() => setAccNameDropdownOpen(false)}
                                getOptionLabel={(opt) => opt?.FirmName || ""}
                                isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                                value={accNameValue}
                                inputValue={accNameInput}
                                onInputChange={(_, newInput) => setAccNameInput(newInput)}
                                onChange={(_, newValue) => setAccNameValue(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search Account Name"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <SearchIcon sx={{ mr: 1, color: T2, fontSize: 18 }} />,
                                            endAdornment: (
                                                <>
                                                    {accNameLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={inputSx}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {/* ── Credit Interest % / Debit Interest % ── */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                Credit Interest %
                            </Typography>
                            <TextField
                                type="number" fullWidth size="small" value={creditInterestPct}
                                onChange={(e) => setCreditInterestPct(e.target.value)}
                                sx={inputSx}
                                InputProps={{ endAdornment: <PercentOutlinedIcon sx={{ fontSize: 15, color: T2 }} /> }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                                Debit Interest %
                            </Typography>
                            <TextField
                                type="number" fullWidth size="small" value={debitInterestPct}
                                onChange={(e) => setDebitInterestPct(e.target.value)}
                                sx={inputSx}
                                InputProps={{ endAdornment: <PercentOutlinedIcon sx={{ fontSize: 15, color: T2 }} /> }}
                            />
                        </Box>
                    </Box>

                    {/* ── Divide Factor ── */}
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                            Divide Factor
                        </Typography>
                        <TextField type="number" fullWidth size="small" value={divideFactor} onChange={(e) => setDivideFactor(e.target.value)} sx={inputSx} />
                    </Box>

                    {/* ── Due Date wise ── */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={dueDateWise}
                                onChange={(e) => setDueDateWise(e.target.checked)}
                                sx={{ color: FB_MID, "&.Mui-checked": { color: FB } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 13, color: T1 }}>Due Date wise</Typography>}
                    />

                    {/* ── Preview ── */}
                    <Box sx={{ background: FB_LIGHT, border: `1px solid ${G300}`, borderRadius: "10px", p: "10px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: G500, textTransform: "uppercase", letterSpacing: ".3px" }}>Preview</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>{fromDate} to {toDate}</Typography>
                            </Box>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>
                                    {mode === "groupwise" ? `Group: ${selectedAccGrpName || "—"}` : `Acc: ${accNameValue?.FirmName || "—"}`}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>Cr {creditInterestPct}% / Dr {debitInterestPct}% / ÷{divideFactor}</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {/* ── Dialog Actions ── */}
            <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={btnGhostSx} fullWidth>
                    Cancel
                </Button>
                <Button onClick={handleOk} disabled={!canSubmit} startIcon={<SearchIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx} fullWidth>
                    Get Data
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── InterestCalculationReport — sidebar-selectable report entry ───────────────
// Mirrors DepreciationReport's initialReportType/openFilterTrigger prop
// contract so AccountReports.js can wire it into the sidebar the same way as
// every other report.
export default function InterestCalculationReport({ connectionString, pdfString, initialReportType, openFilterTrigger }) {
    const { user, selectedCompany } = useAuth();
    const companyId = selectedCompany?.CompId;

    const [filterOpen, setFilterOpen] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportRows, setReportRows] = useState([]);
    const [totalInterest, setTotalInterest] = useState(0);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Same trigger pattern as DepreciationReport/StockReport/LedgerDetails:
    // sidebar click increments openFilterTrigger, which re-opens this
    // report's own dialog.
    useEffect(() => {
        if (openFilterTrigger > 0) {
            setFilterOpen(true);
        }
    }, [openFilterTrigger]);

    // "Get Data" — calls the new /api/interest-calculation endpoint with the
    // dialog's filters and renders the resulting per-account rows.
    const handleOk = async (filters) => {
        setAppliedFilters(filters);
        setReportLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/interest-calculation`, {
                params: {
                    companyId,
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    mode: filters.mode,
                    accGrpId: filters.accGrpId || undefined,
                    partyId: filters.partyId || undefined,
                    creditInterestPct: filters.creditInterestPct,
                    debitInterestPct: filters.debitInterestPct,
                    divideFactor: filters.divideFactor,
                    dueDateWise: filters.dueDateWise ? "1" : "0",
                },
            });

            const data = res.data;
            setReportRows(Array.isArray(data?.data) ? data.data : []);
            setTotalInterest(Number(data?.totalInterest) || 0);
            setSnackbar({
                open: true,
                message: data?.message
                    ? `${data.message} (${data?.data?.length ?? 0} account(s), total interest ${Number(data?.totalInterest) || 0})`
                    : "Interest Calculation report generated.",
                severity: "success",
            });
        } catch (err) {
            setReportRows([]);
            setTotalInterest(0);
            const errMsg = err?.response?.data?.message || err.message || "Failed to generate Interest Calculation report";
            setSnackbar({ open: true, message: errMsg, severity: "error" });
        } finally {
            setReportLoading(false);
        }
    };

    // "Export PDF" — same request shape as Depreciation Report's handleExportPDF
    // (POST ${pdfString}/api/ledger-pdf/<docId> with { docId, userId, data: {...},
    // company: {...} }, responseType: "blob", then opens the returned PDF blob in
    // a new tab) but with its OWN docId ("interest-calculation-report") so the PDF
    // microservice renders this report from its own template, kept separate from
    // Depreciation Report's "depreciation-report" docId.
    const handleExportPDF = async () => {
        if (!appliedFilters || reportRows.length === 0 || pdfLoading) return;

        try {
            setPdfLoading(true);

            const printDate = getTodayDate();

            // Reshape reportRows into the same clientSummaries/entries contract
            // General Ledger's PDF renderer expects, per account.
            const clientSummaries = reportRows.map((acc) => {
                const totalDebit = (acc.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0);
                const totalCredit = (acc.lines || []).reduce((s, l) => s + (Number(l.credit) || 0), 0);
                return {
                    firmName: acc.firmName,
                    clientCode: "",
                    openingBalance: Math.abs(acc.openingBalance),
                    openingType: acc.openingBalance >= 0 ? "Dr" : "Cr",
                    closingBalance: Math.abs(acc.closingBalance),
                    closingType: acc.balanceType,
                    totalDebit,
                    totalCredit,
                    interestAmount: acc.interestAmount,
                    entries: (acc.lines || []).length,
                };
            });

            // Groupwise mode shows a summary-only table (no per-transaction ledger
            // rows), so its PDF export sends only clientSummaries — entries stays
            // empty for that mode instead of flattening every account's lines.
            const entries = appliedFilters.mode === "groupwise" ? [] : reportRows.flatMap((acc) =>
                (acc.lines || []).map((line) => ({
                    clientName: acc.firmName,
                    clientCode: "",
                    date: line.date,
                    particulars: line.bookName || "",
                    remark: line.naration || "",
                    BookName: line.bookName || "",
                    DocNo: "—",
                    debit: line.debit,
                    credit: line.credit,
                    balance: Math.abs(line.closingBalance),
                    balanceType: line.closingBalance >= 0 ? "Dr" : "Cr",
                    interest: line.interest,
                    balDays: line.balDays,
                    clientTotalDebit: (acc.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0),
                    clientTotalCredit: (acc.lines || []).reduce((s, l) => s + (Number(l.credit) || 0), 0),
                }))
            );

            const pdfPayload = {
                docId: "interest-calculation",
                userId: user?.UserId || "usr1",
                data: {
                    reportType: "Interest Calculation Report",
                    accountName: appliedFilters.mode === "groupwise" ? `Group: ${appliedFilters.accGrpLabel}` : `Acc: ${appliedFilters.partyLabel}`,
                    fromDate: appliedFilters.fromDate,
                    toDate: appliedFilters.toDate,
                    printDate,
                    mode: appliedFilters.mode,
                    modeLabel: appliedFilters.mode === "groupwise" ? "Groupwise" : "Accountwise",
                    creditInterestPct: appliedFilters.creditInterestPct,
                    debitInterestPct: appliedFilters.debitInterestPct,
                    divideFactor: appliedFilters.divideFactor,
                    entries,
                    clientSummaries,
                    totalInterest,
                    totalClients: reportRows.length,
                },
                company: selectedCompany
                    ? {
                        CompId: selectedCompany.CompId,
                        CompanyName: selectedCompany.CompanyName || "",
                        Address: selectedCompany.Address || "",
                        MobileNo: selectedCompany.MobileNo || "",
                        GSTIN: selectedCompany.GSTNo || "",
                    }
                    : {},
            };

            console.log("📄 [Interest Calculation Report] Export PDF payload:", pdfPayload);

            const pdfResp = await axios.post(
                `${pdfString}/api/ledger-pdf/interest-calculation`,
                pdfPayload,
                { responseType: "blob" }
            );

            const w = window.open(
                URL.createObjectURL(new Blob([pdfResp.data], { type: "application/pdf" })),
                "_blank"
            );
            if (w) w.focus();
        } catch (err) {
            console.error("PDF Error:", err);
            setSnackbar({ open: true, message: "Failed to generate PDF", severity: "error" });
        } finally {
            setPdfLoading(false);
        }
    };

    const fmtAmt = (v) => (v || v === 0 ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
    const fmtDate = (d) => {
        if (!d) return "";
        const [y, m, day] = String(d).slice(0, 10).split("-");
        return `${day}-${m}-${y.slice(2)}`;
    };

    return (
        <Box sx={{ background: G200, display: "flex", flexDirection: "column", minHeight: "100%" }}>
            {/* ── ERP Header — same design as Depreciation Report / General Ledger ── */}
            <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, display: "flex", alignItems: "stretch", flexShrink: 0, minHeight: 58 }}>
                <Box sx={{ width: 4, background: FB, flexShrink: 0 }} />
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "space-between", px: { xs: 2, sm: 3 }, py: 1.25, gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, background: FB_LIGHT, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <PercentOutlinedIcon sx={{ fontSize: 20, color: FB }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: G900, lineHeight: 1.2 }}>Interest Calculation</Typography>
                            <Typography sx={{ fontSize: 11, color: G500, mt: "1px" }}>
                                {appliedFilters ? `${appliedFilters.fromDate} to ${appliedFilters.toDate} · ${appliedFilters.mode === "groupwise" ? appliedFilters.accGrpLabel : appliedFilters.partyLabel}` : ""}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {/* Export PDF — shown before the Filter button, same order as Depreciation Report */}
                        {appliedFilters && reportRows.length > 0 && (
                            <Button onClick={handleExportPDF} disabled={pdfLoading} startIcon={pdfLoading ? <CircularProgress size={13} sx={{ color: FB }} /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />} sx={btnOutlineSx}>
                                Export PDF
                            </Button>
                        )}
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx}>
                            Smart Filter
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ── Toolbar: filter tags row ── */}
            {appliedFilters && (
                <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, flexShrink: 0 }}>
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, background: G200, borderBottom: `1px solid ${G300}` }}>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 11, color: G500, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".4px", mr: 0.5 }}>Filter:</Typography>
                            {[
                                `${appliedFilters.fromDate} to ${appliedFilters.toDate}`,
                                appliedFilters.mode === "groupwise" ? `Group: ${appliedFilters.accGrpLabel}` : `Acc: ${appliedFilters.partyLabel}`,
                                `Cr ${appliedFilters.creditInterestPct}% / Dr ${appliedFilters.debitInterestPct}%`,
                                `÷${appliedFilters.divideFactor}`,
                                appliedFilters.dueDateWise ? "Due Date wise" : null,
                            ].filter(Boolean).map(tag => (
                                <Box key={tag} sx={{ px: 1.25, py: "2px", background: FB_LIGHT, borderRadius: "4px", border: `1px solid ${G300}` }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: FB }}>{tag}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Button onClick={() => setAppliedFilters(null)} size="small" startIcon={<ClearIcon sx={{ fontSize: 12 }} />}
                            sx={{ color: G700, border: `1px solid ${G300}`, background: "transparent", borderRadius: "6px", fontSize: 11, fontWeight: 500, textTransform: "none", px: 1.25, height: 26, "&:hover": { background: G200 } }}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            )}

            {reportLoading ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: "3rem" }}>
                    <CircularProgress sx={{ color: FB }} />
                </Box>
            ) : appliedFilters && reportRows.length > 0 ? (
                <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ background: FB_LIGHT, border: `1px solid ${G300}`, borderRadius: "8px", px: 2, py: 1.25, mb: 1.5, display: "inline-flex", flexDirection: "column" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: G500, textTransform: "uppercase", letterSpacing: ".4px" }}>Total Interest</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: FB }}>{totalInterest.toFixed(2)}</Typography>
                    </Box>

                    {appliedFilters.mode === "groupwise" ? (
                        // Groupwise mode: summary-only, one row per account —
                        // no per-transaction ledger breakdown.
                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "8px", overflow: "hidden", mb: 2 }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 700, color: G700, textTransform: "uppercase", letterSpacing: ".3px", background: G200, borderBottom: `2px solid ${G300}` } }}>
                                            <TableCell>Acc. Name</TableCell>
                                            <TableCell align="right">Debit</TableCell>
                                            <TableCell align="right">Credit</TableCell>
                                            <TableCell align="right">Closing Balance</TableCell>
                                            <TableCell align="right">Interest</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportRows.map((acc) => {
                                            const totalDebit = (acc.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0);
                                            const totalCredit = (acc.lines || []).reduce((s, l) => s + (Number(l.credit) || 0), 0);
                                            return (
                                                <TableRow key={acc.partyId} sx={{ "& td": { fontSize: 12.5, color: T1, borderBottom: `1px solid ${G200}` } }}>
                                                    <TableCell sx={{ fontWeight: 600, color: G900 }}>{acc.firmName}</TableCell>
                                                    <TableCell align="right">{fmtAmt(totalDebit)}</TableCell>
                                                    <TableCell align="right">{fmtAmt(totalCredit)}</TableCell>
                                                    <TableCell align="right">{fmtAmt(acc.closingBalance)} {acc.balanceType}</TableCell>
                                                    <TableCell align="right">{fmtAmt(acc.interestAmount)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        /* Record-wise breakdown per account — same layout as the
                           printed report: Date / Naration / Bal.Days / Book Name /
                           Debit / Credit / Cl.Bal / Interest, one row per
                           transaction, plus a closing-balance summary row. */
                        reportRows.map((acc) => {
                            const totalDebit = (acc.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0);
                            const totalCredit = (acc.lines || []).reduce((s, l) => s + (Number(l.credit) || 0), 0);
                            return (
                                <Box key={acc.partyId} sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "8px", overflow: "hidden", mb: 2 }}>
                                    <Box sx={{ px: 2, py: 1.25, background: FB_LIGHT, borderBottom: `1px solid ${G300}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: G900, textDecoration: "underline" }}>{acc.firmName}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: FB }}>Interest: {fmtAmt(acc.interestAmount)}</Typography>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 700, color: G700, textTransform: "uppercase", letterSpacing: ".3px", background: G200, borderBottom: `2px solid ${G300}` } }}>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Naration</TableCell>
                                                    <TableCell align="right">Bal. Days</TableCell>
                                                    <TableCell>Book Name</TableCell>
                                                    <TableCell align="right">Debit</TableCell>
                                                    <TableCell align="right">Credit</TableCell>
                                                    <TableCell align="right">Cl. Bal.</TableCell>
                                                    <TableCell align="right">Interest</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(acc.lines || []).map((line) => (
                                                    <TableRow key={line.gLId} sx={{ "& td": { fontSize: 12.5, color: T1, borderBottom: `1px solid ${G200}` } }}>
                                                        <TableCell>{fmtDate(line.date)}</TableCell>
                                                        <TableCell>{line.naration}</TableCell>
                                                        <TableCell align="right">{line.balDays}</TableCell>
                                                        <TableCell>{line.bookName}</TableCell>
                                                        <TableCell align="right">{fmtAmt(line.debit) || "—"}</TableCell>
                                                        <TableCell align="right">{fmtAmt(line.credit) || "—"}</TableCell>
                                                        <TableCell align="right">{fmtAmt(line.closingBalance)}</TableCell>
                                                        <TableCell align="right">{fmtAmt(line.interest) || "—"}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ "& td": { fontSize: 12.5, fontWeight: 700, color: G900, borderTop: `2px solid ${G300}` } }}>
                                                    <TableCell colSpan={2}>CLOSING BALANCE c/f</TableCell>
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell align="right">{fmtAmt(totalDebit)}</TableCell>
                                                    <TableCell align="right">{fmtAmt(totalCredit)}</TableCell>
                                                    <TableCell align="right">{fmtAmt(acc.closingBalance)} {acc.balanceType}</TableCell>
                                                    <TableCell align="right">{fmtAmt(acc.interestAmount)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            );
                        })
                    )}
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <Box sx={{ textAlign: "center", maxWidth: 380 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: "16px", background: FB_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                            <AssessmentOutlinedIcon sx={{ fontSize: 32, color: FB }} />
                        </Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: T1, mb: 1 }}>Interest Calculation</Typography>
                        <Typography sx={{ fontSize: 13, color: T2, lineHeight: 1.7, mb: 2 }}>
                            {appliedFilters
                                ? "No accounts/transactions found for the selected filters."
                                : "Configure the date range, Groupwise/Accountwise selection, and interest rates to view the report."}
                        </Typography>
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 14 }} />} sx={btnPrimarySx}>
                            Configure & Run Report
                        </Button>
                    </Box>
                </Box>
            )}

            <InterestCalculationFilterDialog
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                connectionString={connectionString}
                companyId={companyId}
                onOk={handleOk}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
